// ==================================
// Pagina Nueva Veterinaria
// ==================================

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import { Button, Input, Select, Autocomplete, Card, CardHeader, LogoUpload } from '@/componentes/ui';
import { veterinariasServicio } from '@/servicios';
import { ESTADOS_MEXICO } from '@/constantes/estadosMexico';
import type { CrearVeterinariaInput, TipoLicencia, ModalidadAtencion, LogoInfo, ZonaCobertura, CiudadCobertura } from '@/tipos';

const tipoLicenciaOptions = [
  { value: 'gratis', label: 'Gratis' },
  { value: 'prueba', label: 'Prueba (14 días)' },
  { value: 'mensual', label: 'Mensual' },
];

const SEPOMEX_VERSION = '20241116';

interface SepomexResult {
  colonias: string[];
  municipio: string;
  estado: string;
  ciudad: string;
}

type CpStatus = 'idle' | 'loading' | 'success' | 'error' | 'manual';

async function buscarCodigoPostal(cp: string): Promise<SepomexResult | null> {
  try {
    const res = await fetch(
      `https://sepomex.nitrostudio.com.mx/api/${SEPOMEX_VERSION}/cp/${cp}.json`
    );
    if (!res.ok) return null;

    const data = await res.json();
    if (data.error || !data.data?.postcodes?.length) return null;

    const postcodes = data.data.postcodes;
    const first = postcodes[0];
    const colonias = postcodes.map((p: { d_asenta?: string }) => p.d_asenta || '').filter(Boolean);

    return {
      colonias,
      municipio: first.d_mnpio || '',
      estado: first.d_estado || '',
      ciudad: first.d_ciudad || first.d_mnpio || '',
    };
  } catch {
    return null;
  }
}

async function buscarMunicipios(codigoEstado: string): Promise<string[]> {
  try {
    const res = await fetch(
      `https://sepomex.nitrostudio.com.mx/api/${SEPOMEX_VERSION}/estado/${codigoEstado}.json`
    );
    if (!res.ok) return [];

    const data = await res.json();
    if (data.error || !data.data?.municipios?.length) return [];

    return data.data.municipios
      .map((m: { d_mnpio?: string }) => m.d_mnpio || '')
      .filter(Boolean)
      .sort((a: string, b: string) => a.localeCompare(b, 'es'));
  } catch {
    return [];
  }
}

// ── Iconos ──
const iconoFisico = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);
const iconoDomicilio = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);
const iconoAmbos = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const MODALIDADES: { value: ModalidadAtencion; label: string; desc: string; icon: React.ReactNode }[] = [
  { value: 'fisico', label: 'Lugar físico', desc: 'Clínica o consultorio', icon: iconoFisico },
  { value: 'domicilio', label: 'A domicilio', desc: 'Visitas en casa del cliente', icon: iconoDomicilio },
  { value: 'ambos', label: 'Ambos', desc: 'Clínica + visitas a domicilio', icon: iconoAmbos },
];

let zonaIdCounter = 0;
function generarZonaId() {
  return `zona_${++zonaIdCounter}`;
}

// ─────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────
export function NuevaVeterinaria() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CrearVeterinariaInput>({
    nombre: '',
    slug: '',
    email: '',
    telefono: '',
    direccion: {
      calle: '',
      numeroExterior: '',
      numeroInterior: '',
      colonia: '',
      ciudad: '',
      estado: '',
      codigoPostal: '',
      pais: 'México',
    },
    modalidadAtencion: 'fisico',
    zonasCobertura: [],
    tipoLicencia: 'prueba' as TipoLicencia,
    adminNombre: '',
    adminApellidoPaterno: '',
    adminApellidoMaterno: '',
    adminEmail: '',
    adminPassword: '',
    adminTelefono: '',
  });

  const [logo, setLogo] = useState<LogoInfo | undefined>();
  const [esGratis, setEsGratis] = useState(false);
  const [motivoGratis, setMotivoGratis] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── CP autocomplete state ──
  const [cpStatus, setCpStatus] = useState<CpStatus>('idle');
  const [cpColonias, setCpColonias] = useState<string[]>([]);

  // ── Zonas de cobertura: formulario inline ──
  const [zonaFormOpen, setZonaFormOpen] = useState(false);
  const [zonaEstado, setZonaEstado] = useState('');
  const [zonaTodoEstado, setZonaTodoEstado] = useState(false);
  const [zonaCiudadInput, setZonaCiudadInput] = useState('');
  const [zonaCiudades, setZonaCiudades] = useState<CiudadCobertura[]>([]);
  const [zonaMunicipios, setZonaMunicipios] = useState<string[]>([]);
  const [zonaMunicipiosLoading, setZonaMunicipiosLoading] = useState(false);

  const requiereDireccionFisica = formData.modalidadAtencion !== 'domicilio';
  const muestraZonas = formData.modalidadAtencion !== 'fisico';
  const zonas = formData.zonasCobertura || [];

  // Cargar municipios cuando cambia el estado seleccionado en zona
  useEffect(() => {
    if (!zonaEstado) { setZonaMunicipios([]); return; }
    const estadoObj = ESTADOS_MEXICO.find((e) => e.nombre === zonaEstado);
    if (!estadoObj) return;

    setZonaMunicipiosLoading(true);
    buscarMunicipios(estadoObj.codigo).then((munis) => {
      setZonaMunicipios(munis);
      setZonaMunicipiosLoading(false);
    });
  }, [zonaEstado]);

  const crearMutation = useMutation({
    mutationFn: () => {
      const datos: CrearVeterinariaInput = {
        ...formData,
        esGratis,
        motivoGratis: esGratis ? motivoGratis : undefined,
        logo: logo || undefined,
        tipoLicencia: esGratis ? ('gratis' as TipoLicencia) : formData.tipoLicencia,
      };
      return veterinariasServicio.crear(datos);
    },
    onSuccess: (data) => {
      toast.success('Veterinaria creada exitosamente');
      if (data.linkPagoKit) {
        navigator.clipboard.writeText(data.linkPagoKit);
        toast.success('Link de pago del kit copiado al portapapeles', { duration: 8000 });
      }
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      navigate('/veterinarias');
    },
    onError: (error: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = (error as any)?.response?.data;
      if (resp?.error?.detalles?.length) {
        const detalles = resp.error.detalles as { campo: string; mensaje: string }[];
        const newErrors: Record<string, string> = {};
        detalles.forEach((d) => {
          // Mapear campos como "direccion.calle" a la key "calle"
          const key = d.campo.includes('.') ? d.campo.split('.').pop()! : d.campo;
          newErrors[key] = d.mensaje;
        });
        setErrors(newErrors);
        toast.error(resp.error.mensaje || 'Error de validación');
      } else {
        toast.error('Error al crear la veterinaria');
      }
    },
  });

  // ── Handlers genéricos ──
  const handleChange = (field: string, value: string, isAddress = false) => {
    if (isAddress) {
      setFormData((prev) => ({ ...prev, direccion: { ...prev.direccion, [field]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const generateSlug = (nombre: string) =>
    nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const handleNombreChange = (nombre: string) => {
    handleChange('nombre', nombre);
    if (!formData.slug || formData.slug === generateSlug(formData.nombre)) {
      handleChange('slug', generateSlug(nombre));
    }
  };

  const handleTipoLicenciaChange = (value: string) => {
    if (value === 'gratis') { setEsGratis(true); handleChange('tipoLicencia', 'gratis'); }
    else { setEsGratis(false); setMotivoGratis(''); handleChange('tipoLicencia', value); }
  };

  const handleModalidadChange = (value: ModalidadAtencion) => {
    setFormData((prev) => ({ ...prev, modalidadAtencion: value }));
    setErrors((prev) => { const n = { ...prev }; delete n.calle; delete n.numeroExterior; delete n.colonia; delete n.zonasCobertura; return n; });
  };

  // ── CP lookup ──
  const handleCpChange = useCallback(async (value: string) => {
    const cp = value.replace(/\D/g, '').slice(0, 5);
    handleChange('codigoPostal', cp, true);
    if (cp.length < 5) { setCpStatus('idle'); setCpColonias([]); return; }
    setCpStatus('loading');
    const result = await buscarCodigoPostal(cp);
    if (result && result.colonias.length > 0) {
      setCpStatus('success');
      setCpColonias(result.colonias);
      setFormData((prev) => ({
        ...prev,
        direccion: { ...prev.direccion, codigoPostal: cp, colonia: result.colonias.length === 1 ? result.colonias[0] : '', ciudad: result.ciudad, estado: result.estado },
      }));
    } else { setCpStatus('error'); setCpColonias([]); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLlenarManual = () => { setCpStatus('manual'); setCpColonias([]); };
  const camposDeshabilitados = cpStatus === 'success';

  // ── Zonas de cobertura handlers ──
  const resetZonaForm = () => {
    setZonaFormOpen(false);
    setZonaEstado('');
    setZonaTodoEstado(false);
    setZonaCiudadInput('');
    setZonaCiudades([]);
    setZonaMunicipios([]);
  };

  const agregarCiudadAZona = (nombre?: string) => {
    const ciudad = (nombre || zonaCiudadInput).trim();
    if (!ciudad || zonaCiudades.some((c) => c.nombre.toLowerCase() === ciudad.toLowerCase())) return;
    setZonaCiudades((prev) => [...prev, { nombre: ciudad, todaLaCiudad: true, colonias: [] }]);
    setZonaCiudadInput('');
  };

  const eliminarCiudadDeZona = (idx: number) => {
    setZonaCiudades((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleCiudadCobertura = (idx: number) => {
    setZonaCiudades((prev) => prev.map((c, i) => i === idx ? { ...c, todaLaCiudad: !c.todaLaCiudad } : c));
  };

  const guardarZona = () => {
    if (!zonaEstado) return;
    if (!zonaTodoEstado && zonaCiudades.length === 0) {
      toast.error('Agrega al menos una ciudad o selecciona "Todo el estado"');
      return;
    }
    const nuevaZona: ZonaCobertura = {
      id: generarZonaId(),
      estado: zonaEstado,
      todoElEstado: zonaTodoEstado,
      ciudades: zonaTodoEstado ? [] : zonaCiudades,
    };
    setFormData((prev) => ({ ...prev, zonasCobertura: [...(prev.zonasCobertura || []), nuevaZona] }));
    resetZonaForm();
  };

  const eliminarZona = (id: string) => {
    setFormData((prev) => ({ ...prev, zonasCobertura: (prev.zonasCobertura || []).filter((z) => z.id !== id) }));
  };

  // Estados ya usados (para no repetir)
  const estadosUsados = new Set(zonas.map((z) => z.estado));

  // ── Validación ──
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.slug.trim()) { newErrors.slug = 'El slug es requerido'; }
    else if (!/^[a-z0-9-]+$/.test(formData.slug)) { newErrors.slug = 'Solo letras minúsculas, números y guiones'; }
    if (!formData.email.trim()) { newErrors.email = 'El email es requerido'; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { newErrors.email = 'Email inválido'; }
    if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es requerido';

    // Dirección física
    if (requiereDireccionFisica) {
      if (!formData.direccion.calle.trim()) newErrors.calle = 'La calle es requerida';
      if (!formData.direccion.numeroExterior.trim()) newErrors.numeroExterior = 'El número exterior es requerido';
      if (!formData.direccion.colonia.trim()) newErrors.colonia = 'La colonia es requerida';
      if (!formData.direccion.codigoPostal.trim()) newErrors.codigoPostal = 'El código postal es requerido';
      if (!formData.direccion.ciudad.trim()) newErrors.ciudad = 'La ciudad es requerida';
      if (!formData.direccion.estado.trim()) newErrors.estado = 'El estado es requerido';
    }

    // Zonas de cobertura (requeridas para domicilio y ambos)
    if (muestraZonas && zonas.length === 0) {
      newErrors.zonasCobertura = 'Agrega al menos una zona de cobertura';
    }

    // Admin
    if (!formData.adminNombre.trim()) newErrors.adminNombre = 'El nombre del administrador es requerido';
    if (!formData.adminApellidoPaterno.trim()) newErrors.adminApellidoPaterno = 'El apellido paterno es requerido';
    if (!formData.adminEmail.trim()) { newErrors.adminEmail = 'El email del administrador es requerido'; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) { newErrors.adminEmail = 'Email inválido'; }
    if (!formData.adminPassword.trim()) { newErrors.adminPassword = 'La contraseña es requerida'; }
    else if (formData.adminPassword.length < 8) { newErrors.adminPassword = 'La contraseña debe tener al menos 8 caracteres'; }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) crearMutation.mutate();
  };

  const selectedTipoValue = esGratis ? 'gratis' : formData.tipoLicencia;

  // ── CP suffix ──
  const cpSuffix = (() => {
    if (cpStatus === 'loading') return (
      <svg className="w-5 h-5 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    );
    if (cpStatus === 'success') return (
      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
    if (cpStatus === 'error') return (
      <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    );
    return undefined;
  })();

  // ── Helper: descripción de zona para chip ──
  function descZona(z: ZonaCobertura): string {
    if (z.todoElEstado) return `${z.estado} — Todo el estado`;
    const parts = z.ciudades.map((c) => {
      if (c.todaLaCiudad) return c.nombre;
      return `${c.nombre} (${c.colonias.length} col.)`;
    });
    return `${z.estado} — ${parts.join(', ')}`;
  }

  return (
    <div>
      <Header titulo="Nueva Veterinaria" />

      <div className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ─── Informacion General ─── */}
            <Card>
              <CardHeader title="Información General" subtitle="Datos básicos de la veterinaria" />
              <div className="space-y-4">
                <LogoUpload currentLogo={logo} onUpload={(l) => setLogo(l)} onRemove={() => setLogo(undefined)} />
                <Input label="Nombre de la veterinaria" value={formData.nombre} onChange={(e) => handleNombreChange(e.target.value)} error={errors.nombre} placeholder="Veterinaria Patitas Felices" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>} />
                <Input label="Slug (subdominio)" value={formData.slug} onChange={(e) => handleChange('slug', e.target.value)} error={errors.slug} placeholder="mi-veterinaria" helperText="Se usará como: slug.alebrijecloud.com" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} error={errors.email} placeholder="contacto@veterinaria.com" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>} />
                  <Input label="Teléfono" value={formData.telefono} onChange={(e) => handleChange('telefono', e.target.value)} error={errors.telefono} placeholder="55 1234 5678" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>} />
                </div>
                <Select label="Tipo de licencia" options={tipoLicenciaOptions} value={selectedTipoValue} onChange={(e) => handleTipoLicenciaChange(e.target.value)} />
                {esGratis && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium mb-2">Esta veterinaria no tendrá cobros ni UI de renovación</p>
                    <Input label="Motivo de la gratuidad" value={motivoGratis} onChange={(e) => setMotivoGratis(e.target.value)} placeholder="Ej: Veterinaria de prueba interna, cortesía, etc." />
                  </div>
                )}
                {!esGratis && formData.tipoLicencia === 'mensual' && (
                  <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-sm text-primary-700">Al crear esta veterinaria se generará un link de pago para el Kit de Instalación.</p>
                  </div>
                )}
              </div>
            </Card>

            {/* ─── Ubicación ─── */}
            <Card>
              <CardHeader title="Ubicación" subtitle="Tipo de atención y dirección" />
              <div className="space-y-4">

                {/* Selector de modalidad */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Modalidad de atención</label>
                  <div className="grid grid-cols-3 gap-2">
                    {MODALIDADES.map((m) => {
                      const sel = formData.modalidadAtencion === m.value;
                      return (
                        <button key={m.value} type="button" onClick={() => handleModalidadChange(m.value)}
                          className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-200 text-center ${sel ? 'border-primary-500 bg-primary-50 shadow-sm shadow-primary-100' : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50'}`}>
                          <div className={`${sel ? 'text-primary-600' : 'text-stone-400'} transition-colors`}>{m.icon}</div>
                          <span className={`text-xs font-semibold ${sel ? 'text-primary-700' : 'text-stone-700'}`}>{m.label}</span>
                          <span className={`text-[10px] leading-tight ${sel ? 'text-primary-500' : 'text-stone-400'}`}>{m.desc}</span>
                          {sel && (
                            <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Info contextual */}
                {formData.modalidadAtencion === 'domicilio' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p className="text-sm text-blue-700">Define las zonas donde ofrece servicio a domicilio.</p>
                  </div>
                )}
                {formData.modalidadAtencion === 'ambos' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p className="text-sm text-blue-700">Ingresa la dirección de la clínica. Abajo podrás agregar las zonas de cobertura a domicilio.</p>
                  </div>
                )}

                {/* ── Dirección física (fisico y ambos) ── */}
                {requiereDireccionFisica && (
                  <>
                    <Input label="Código Postal" value={formData.direccion.codigoPostal} onChange={(e) => handleCpChange(e.target.value)} error={errors.codigoPostal} placeholder="Ej: 06600" maxLength={5} suffix={cpSuffix} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>} />

                    {cpStatus === 'success' && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        <div>
                          <p className="text-sm text-green-700 font-medium">Dirección encontrada: {formData.direccion.ciudad}, {formData.direccion.estado}</p>
                          <button type="button" onClick={handleLlenarManual} className="text-xs text-green-600 underline mt-0.5">Corregir manualmente</button>
                        </div>
                      </div>
                    )}
                    {cpStatus === 'error' && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <svg className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                        <div>
                          <p className="text-sm text-amber-700 font-medium">No se encontró información para este C.P.</p>
                          <button type="button" onClick={handleLlenarManual} className="text-xs text-amber-600 underline mt-0.5">Llenar manualmente</button>
                        </div>
                      </div>
                    )}

                    {cpStatus === 'success' && cpColonias.length > 1 ? (
                      <Autocomplete label="Colonia" options={cpColonias.map((c) => ({ value: c, label: c }))} value={formData.direccion.colonia} onChange={(val) => handleChange('colonia', val, true)} error={errors.colonia} placeholder="Buscar colonia..." icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>} />
                    ) : (
                      <Input label="Colonia" value={formData.direccion.colonia} onChange={(e) => handleChange('colonia', e.target.value, true)} error={errors.colonia} placeholder="Centro" disabled={cpStatus === 'success' && cpColonias.length === 1} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>} />
                    )}

                    <Input label="Calle" value={formData.direccion.calle} onChange={(e) => handleChange('calle', e.target.value, true)} error={errors.calle} placeholder="Av. Reforma" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>} />
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="Num. Exterior" value={formData.direccion.numeroExterior} onChange={(e) => handleChange('numeroExterior', e.target.value, true)} error={errors.numeroExterior} placeholder="123" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>} />
                      <Input label="Num. Interior" value={formData.direccion.numeroInterior || ''} onChange={(e) => handleChange('numeroInterior', e.target.value, true)} placeholder="A (opcional)" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label="Ciudad" value={formData.direccion.ciudad} onChange={(e) => handleChange('ciudad', e.target.value, true)} error={errors.ciudad} placeholder="Ciudad de México" disabled={camposDeshabilitados} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>} />
                      <Input label="Estado" value={formData.direccion.estado} onChange={(e) => handleChange('estado', e.target.value, true)} error={errors.estado} placeholder="CDMX" disabled={camposDeshabilitados} icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>} />
                    </div>
                  </>
                )}

                {/* ── Zonas de cobertura (domicilio y ambos) ── */}
                {muestraZonas && (
                  <div className="pt-2">
                    {/* Separador visual si hay dirección arriba */}
                    {requiereDireccionFisica && <div className="border-t border-stone-200 mb-4" />}

                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-stone-700">Zonas de cobertura</p>
                        <p className="text-xs text-stone-400">Áreas donde ofrece servicio a domicilio</p>
                      </div>
                      {!zonaFormOpen && (
                        <button
                          type="button"
                          onClick={() => setZonaFormOpen(true)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          Agregar zona
                        </button>
                      )}
                    </div>

                    {errors.zonasCobertura && (
                      <p className="text-sm text-red-600 mb-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {errors.zonasCobertura}
                      </p>
                    )}

                    {/* Zonas agregadas */}
                    {zonas.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {zonas.map((z) => (
                          <div key={z.id} className="flex items-center gap-2 p-2.5 bg-stone-50 border border-stone-200 rounded-xl group">
                            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                              <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <p className="text-sm text-stone-700 flex-1 truncate">{descZona(z)}</p>
                            <button
                              type="button"
                              onClick={() => eliminarZona(z.id)}
                              className="p-1 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Empty state */}
                    {zonas.length === 0 && !zonaFormOpen && (
                      <div className="text-center py-6 border-2 border-dashed border-stone-200 rounded-xl">
                        <svg className="w-8 h-8 text-stone-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        <p className="text-sm text-stone-400">Sin zonas de cobertura</p>
                        <button
                          type="button"
                          onClick={() => setZonaFormOpen(true)}
                          className="mt-2 text-xs font-semibold text-primary-600 hover:text-primary-700"
                        >
                          Agregar primera zona
                        </button>
                      </div>
                    )}

                    {/* ── Formulario inline para agregar zona ── */}
                    {zonaFormOpen && (
                      <div className="border-2 border-primary-200 bg-primary-50/30 rounded-xl p-4 space-y-3">
                        <p className="text-sm font-semibold text-stone-800">Nueva zona de cobertura</p>

                        {/* Paso 1: Estado */}
                        <Autocomplete
                          label="Estado"
                          options={ESTADOS_MEXICO.filter((e) => !estadosUsados.has(e.nombre)).map((e) => ({ value: e.nombre, label: e.nombre }))}
                          value={zonaEstado}
                          onChange={(val) => { setZonaEstado(val); setZonaTodoEstado(false); setZonaCiudades([]); }}
                          placeholder="Buscar estado..."
                          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>}
                        />

                        {/* Paso 2: Todo el estado o ciudades */}
                        {zonaEstado && (
                          <>
                            <label className="flex items-center gap-3 p-3 bg-white border border-stone-200 rounded-xl cursor-pointer hover:border-primary-300 transition-colors">
                              <input
                                type="checkbox"
                                checked={zonaTodoEstado}
                                onChange={(e) => { setZonaTodoEstado(e.target.checked); if (e.target.checked) setZonaCiudades([]); }}
                                className="w-4 h-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div>
                                <p className="text-sm font-medium text-stone-700">Todo el estado de {zonaEstado}</p>
                                <p className="text-xs text-stone-400">Cubre todas las ciudades y colonias</p>
                              </div>
                            </label>

                            {/* Agregar ciudades específicas */}
                            {!zonaTodoEstado && (
                              <>
                                {zonaMunicipiosLoading ? (
                                  <div className="flex items-center gap-2 p-3 bg-white border border-stone-200 rounded-xl">
                                    <svg className="w-4 h-4 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    <span className="text-sm text-stone-500">Cargando municipios...</span>
                                  </div>
                                ) : (
                                  <Autocomplete
                                    label="Agregar ciudad / municipio"
                                    options={zonaMunicipios
                                      .filter((m) => !zonaCiudades.some((c) => c.nombre === m))
                                      .map((m) => ({ value: m, label: m }))}
                                    value={zonaCiudadInput}
                                    onChange={(val) => { agregarCiudadAZona(val); }}
                                    placeholder="Buscar municipio..."
                                    icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                                  />
                                )}

                                {/* Ciudades agregadas */}
                                {zonaCiudades.length > 0 && (
                                  <div className="space-y-1.5">
                                    {zonaCiudades.map((c, idx) => (
                                      <div key={idx} className="flex items-center gap-2 p-2 bg-white border border-stone-200 rounded-lg">
                                        <button type="button" onClick={() => toggleCiudadCobertura(idx)} className={`shrink-0 px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${c.todaLaCiudad ? 'bg-primary-100 text-primary-700' : 'bg-stone-100 text-stone-500'}`}>
                                          {c.todaLaCiudad ? 'TODA' : 'PARCIAL'}
                                        </button>
                                        <span className="text-sm text-stone-700 flex-1">{c.nombre}</span>
                                        <button type="button" onClick={() => eliminarCiudadDeZona(idx)} className="p-0.5 text-stone-400 hover:text-red-500 transition-colors">
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {/* Acciones del form de zona */}
                        <div className="flex gap-2 pt-1">
                          <button type="button" onClick={resetZonaForm} className="px-3 py-1.5 text-xs font-medium text-stone-600 bg-white border border-stone-300 hover:bg-stone-50 rounded-lg transition-colors">
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={guardarZona}
                            disabled={!zonaEstado || (!zonaTodoEstado && zonaCiudades.length === 0)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:bg-stone-300 rounded-lg transition-colors"
                          >
                            Agregar zona
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </Card>

            {/* ─── Administrador ─── */}
            <Card className="lg:col-span-2">
              <CardHeader title="Administrador" subtitle="Usuario que administrará la veterinaria" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input label="Nombre" value={formData.adminNombre} onChange={(e) => handleChange('adminNombre', e.target.value)} error={errors.adminNombre} placeholder="Juan" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} />
                <Input label="Apellido Paterno" value={formData.adminApellidoPaterno} onChange={(e) => handleChange('adminApellidoPaterno', e.target.value)} error={errors.adminApellidoPaterno} placeholder="Pérez" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} />
                <Input label="Apellido Materno" value={formData.adminApellidoMaterno || ''} onChange={(e) => handleChange('adminApellidoMaterno', e.target.value)} placeholder="García (opcional)" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} />
                <Input label="Email del Administrador" type="email" value={formData.adminEmail} onChange={(e) => handleChange('adminEmail', e.target.value)} error={errors.adminEmail} placeholder="admin@veterinaria.com" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>} />
                <Input label="Contraseña" type="password" value={formData.adminPassword} onChange={(e) => handleChange('adminPassword', e.target.value)} error={errors.adminPassword} placeholder="Mínimo 8 caracteres" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>} />
                <Input label="Teléfono (opcional)" value={formData.adminTelefono || ''} onChange={(e) => handleChange('adminTelefono', e.target.value)} placeholder="55 1234 5678" icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>} />
              </div>
            </Card>
          </div>

          {/* Acciones */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <Button type="button" variant="secondary" onClick={() => navigate('/veterinarias')} leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={crearMutation.isPending} leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}>
              Crear Veterinaria
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NuevaVeterinaria;
